import { useQuery } from "@tanstack/react-query";
import { eachDayOfInterval, endOfDay, format, startOfDay } from "date-fns";

import { homeQueryKeys } from "@/features/home/api/queryKeys";
import { instance } from "@/lib/axios/instance";

export const MAX_SEGMENT_MEMBERSHIP_QUERIES = 20;

interface MembershipSnapshot {
  segmentId: string;
  vacancyIds: string[];
  asOfUtc: string;
  version: number;
}

interface SegmentInput {
  id: string;
  name: string;
  baselineCount?: number;
}

export interface SegmentCountPoint {
  segmentId: string;
  segmentName: string;
  day: string;
  count: number | null;
}

interface MembershipHistoryBatchResponse {
  results: Record<string, MembershipSnapshot[]>;
  truncatedSegmentIds: string[];
}

interface UseSegmentMembershipHistoryParams {
  segments: SegmentInput[];
  from: Date;
  to: Date;
  clientId?: string;
}

export function useSegmentMembershipHistory({
  segments,
  from,
  to,
  clientId,
}: UseSegmentMembershipHistoryParams) {
  const cappedSegments = segments.slice(0, MAX_SEGMENT_MEMBERSHIP_QUERIES);

  const fromLocal = startOfDay(from);
  const toLocal = endOfDay(to);
  const fromIso = fromLocal.toISOString();
  const toIso = toLocal.toISOString();

  const dayDates = eachDayOfInterval({ start: fromLocal, end: startOfDay(to) });
  const days = dayDates.map((d) => format(d, "yyyy-MM-dd"));
  const dayEndsMs = dayDates.map((d) => endOfDay(d).getTime());

  const segmentIds = cappedSegments.map((s) => s.id);

  const { data: batchData, isLoading, isError, error } = useQuery({
    queryKey: homeQueryKeys.segmentMembershipHistoryBatch(
      segmentIds,
      fromIso,
      toIso,
      clientId,
    ),
    queryFn: async () => {
      const res = await instance.post<MembershipHistoryBatchResponse>(
        "/api/v2/Segments/membership/history/batch",
        {
          segmentIds,
          from: fromIso,
          to: toIso,
        },
      );
      return res.data;
    },
    enabled: cappedSegments.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const series = cappedSegments.map((segment) => {
    const snapshots = (batchData?.results?.[segment.id] ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(a.asOfUtc).getTime() - new Date(b.asOfUtc).getTime(),
      );

    let snapshotCursor = 0;
    let lastCount: number | null = null;

    const points: SegmentCountPoint[] = days.map((day, dayIndex) => {
      const dayEnd = dayEndsMs[dayIndex];
      while (
        snapshotCursor < snapshots.length &&
        new Date(snapshots[snapshotCursor].asOfUtc).getTime() <= dayEnd
      ) {
        lastCount = snapshots[snapshotCursor].vacancyIds.length;
        snapshotCursor++;
      }
      return {
        segmentId: segment.id,
        segmentName: segment.name,
        day,
        count: lastCount,
      };
    });

    return { segment, points };
  });

  const droppedCount = segments.length - cappedSegments.length;
  const truncatedSegmentIds = batchData?.truncatedSegmentIds ?? [];

  return { series, days, isLoading, isError, error, droppedCount, truncatedSegmentIds };
}
